import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  FileText,
  Check,
  X,
  Upload,
  Download,
  Clock,
  AlertCircle,
  CheckCircle,
  ChevronRight,
  Building2,
  Truck,
  ClipboardList,
  Send,
  Lock,
  Unlock,
  Eye,
  Loader2,
  LogOut,
  User,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import ReportProblemButton from "../components/ReportProblemButton";
import { API_URL, API_BASE_URL } from "../config/api";


// Simple header for Trucking Portal (no burger menu, no post-it notes)
const TruckingPortalHeader = ({ user, onLogout }) => (
  <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 shadow-sm">
    <div className="container mx-auto px-4 h-14 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="bg-blue-600 p-2 rounded-lg">
          <Truck className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Trucking Partner Portal</h1>
          <p className="text-xs text-slate-500">Europacific Logistics</p>
        </div>
      </div>
      
      <div className="flex items-center gap-4">
        <ReportProblemButton />
        {user && (
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-slate-700">{user.company_name || user.name || 'Partner'}</p>
              <p className="text-xs text-slate-500">{user.email}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={onLogout} className="text-slate-600 hover:text-red-600">
              <LogOut className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        )}
      </div>
    </div>
  </header>
);

// Default RFI Questions based on Keelvar-style tendering
const DEFAULT_RFI_QUESTIONS = [
  // Gating Questions (must answer Yes to access bid sheet)
  { id: "q1", number: "1", text: "Are you interested in participating in this tender?", type: "yes_no", gating: true, mandatory: true },
  { id: "q1_1", number: "1.1", text: "Do you accept signing a 1-year contract and respecting all contract conditions?", type: "yes_no", gating: true, mandatory: true },
  { id: "q1_2", number: "1.2", text: "Can you confirm that you can start cooperation within 1 month since the beginning of contract?", type: "yes_no", gating: true, mandatory: true },
  { id: "q3", number: "3", text: "Do you commit to hold confidentiality to all the information supplied?", type: "yes_no", gating: true, mandatory: true },
  { id: "q9", number: "9", text: "Do you confirm you will provide rates for all destinations within your zone of interest?", type: "yes_no", gating: true, mandatory: true },
  
  // Company Information (Non-gating)
  { id: "q2_1", number: "2.1", text: "Company Full Name", type: "text", gating: false, mandatory: true },
  { id: "q2_2", number: "2.2", text: "Country", type: "text", gating: false, mandatory: true },
  { id: "q2_3", number: "2.3", text: "Contact Person (Name, Email, Phone)", type: "text", gating: false, mandatory: true },
  
  // Fleet Information
  { id: "q7", number: "7", text: "Are you a Carrier with own fleet?", type: "yes_no", gating: false, mandatory: false },
  { id: "q7_1", number: "7.1", text: "Own fleet size (number of trucks)", type: "number", gating: false, mandatory: false },
  { id: "q7_2", number: "7.2", text: "Fleet Size - Trailer TILT", type: "number", gating: false, mandatory: false },
  { id: "q7_3", number: "7.3", text: "Fleet Size - Trailer MEGA", type: "number", gating: false, mandatory: false },
  
  // Operational Questions
  { id: "q8", number: "8", text: "Are you able to provide Intermodal/Multimodal solutions?", type: "yes_no", gating: false, mandatory: false },
  { id: "q9_1", number: "9.1", text: "Can you confirm you will update order status/Track & Trace twice daily?", type: "yes_no", gating: false, mandatory: true },
  { id: "q9_2", number: "9.2", text: "Do you accept penalty terms for late OTP/OTD and Non-Show fees?", type: "yes_no", gating: false, mandatory: true },
  { id: "q9_3", number: "9.3", text: "Do you accept FREE WAITING HOURS at loading/unloading (2 hours)?", type: "yes_no", gating: false, mandatory: true },
  { id: "q9_4", number: "9.4", text: "Do you confirm payment term of 40 days after receipt of correct invoice?", type: "yes_no", gating: false, mandatory: true },
  
  // Capacity
  { id: "q12", number: "12", text: "Will you provide capacity during peak seasons?", type: "yes_no", gating: false, mandatory: true },
  { id: "q12_1", number: "12.1", text: "Will you provide capacity on weekends?", type: "yes_no", gating: false, mandatory: false },
  
  // File Uploads
  { id: "q4", number: "4", text: "Please upload signed NDA (if required)", type: "file", gating: false, mandatory: false },
  { id: "q6_1", number: "6.1", text: "Please upload credit rating report (max 3 months old)", type: "file", gating: false, mandatory: false },
];

export default function TruckingPortalPage() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("invitations");
  const [tenderInvitations, setTenderInvitations] = useState([]);
  const [selectedTender, setSelectedTender] = useState(null);
  const [rfiAnswers, setRfiAnswers] = useState({});
  const [bidSheetData, setBidSheetData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submittingRfi, setSubmittingRfi] = useState(false);
  const [submittingBid, setSubmittingBid] = useState(false);
  const [rfiComplete, setRfiComplete] = useState(false);
  const [bidSheetUnlocked, setBidSheetUnlocked] = useState(false);

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Fetch tender invitations for this trucking company
  const fetchInvitations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/trucking-portal/invitations`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTenderInvitations(response.data.invitations || []);
    } catch (error) {
      console.log("Could not fetch invitations");
      // For demo, show mock data
      setTenderInvitations([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  // Check if all gating questions are answered "Yes"
  const checkGatingQuestions = () => {
    const gatingQuestions = DEFAULT_RFI_QUESTIONS.filter(q => q.gating);
    const allGatingPassed = gatingQuestions.every(q => rfiAnswers[q.id] === "yes");
    return allGatingPassed;
  };

  // Check if all mandatory questions are answered
  const checkMandatoryQuestions = () => {
    const mandatoryQuestions = DEFAULT_RFI_QUESTIONS.filter(q => q.mandatory);
    return mandatoryQuestions.every(q => {
      const answer = rfiAnswers[q.id];
      if (q.type === "yes_no") return answer === "yes" || answer === "no";
      if (q.type === "text" || q.type === "number") return answer && answer.trim() !== "";
      return true; // Files are optional even if marked mandatory for now
    });
  };

  // Submit RFI
  const submitRfi = async () => {
    if (!checkMandatoryQuestions()) {
      toast.error("Please answer all mandatory questions");
      return;
    }

    const gatingPassed = checkGatingQuestions();
    if (!gatingPassed) {
      toast.error("You must answer 'Yes' to all gating questions to access the Bid Sheet");
      return;
    }

    setSubmittingRfi(true);
    try {
      await axios.post(
        `${API_URL}/trucking-portal/submit-rfi`,
        {
          tender_id: selectedTender?.id,
          answers: rfiAnswers,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRfiComplete(true);
      setBidSheetUnlocked(true);
      toast.success("RFI submitted successfully! Bid Sheet is now unlocked.");
      setActiveTab("bidsheet");
    } catch (error) {
      toast.error("Failed to submit RFI");
    } finally {
      setSubmittingRfi(false);
    }
  };

  // Download bid sheet template
  const downloadBidTemplate = () => {
    if (!selectedTender) return;
    
    // Create CSV content
    const headers = ["Route ID", "From", "To", "ZIP Code", "Delivery Type", "Equipment", "Transport Mode", "Est. Annual Volume", "Est. Weekly Volume", "Complete Bid (Yes/No)", "Your Rate (EUR)", "Weekly Capacity"];
    const routes = selectedTender.routes || [];
    
    let csv = headers.join(",") + "\n";
    routes.forEach((route, idx) => {
      csv += `${idx + 1},${route.from || ""},${route.to || ""},${route.zip_code || ""},${route.delivery_type || "DIR"},${route.equipment || "TILT"},${route.transport_mode || "ROAD"},${route.annual_volume || ""},${route.weekly_volume || ""},,\n`;
    });
    
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bid_sheet_${selectedTender.name?.replace(/\s+/g, "_") || "tender"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Bid sheet template downloaded");
  };

  // Handle bid file upload
  const handleBidUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result;
      const lines = text.split("\n").filter(line => line.trim());
      
      // Skip header row
      const dataLines = lines.slice(1);
      const parsedBids = dataLines.map((line, idx) => {
        const cols = line.split(",").map(s => s.trim());
        return {
          route_id: cols[0] || idx + 1,
          from: cols[1] || "",
          to: cols[2] || "",
          zip_code: cols[3] || "",
          delivery_type: cols[4] || "DIR",
          equipment: cols[5] || "TILT",
          transport_mode: cols[6] || "ROAD",
          annual_volume: cols[7] || "",
          weekly_volume: cols[8] || "",
          complete_bid: cols[9]?.toLowerCase() === "yes",
          rate: parseFloat(cols[10]) || null,
          weekly_capacity: parseInt(cols[11]) || null,
        };
      }).filter(b => b.from || b.to);

      setBidSheetData(parsedBids);
      toast.success(`Loaded ${parsedBids.length} routes from file`);
    };
    reader.readAsText(file);
  };

  // Submit bid
  const submitBid = async () => {
    const bidsToSubmit = bidSheetData.filter(b => b.complete_bid && b.rate);
    
    if (bidsToSubmit.length === 0) {
      toast.error("Please provide at least one rate in your bid");
      return;
    }

    setSubmittingBid(true);
    try {
      await axios.post(
        `${API_URL}/trucking-portal/submit-bid`,
        {
          tender_id: selectedTender?.id,
          bids: bidsToSubmit,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Bid submitted with ${bidsToSubmit.length} routes!`);
      fetchInvitations();
    } catch (error) {
      toast.error("Failed to submit bid");
    } finally {
      setSubmittingBid(false);
    }
  };

  // Open tender details
  const openTender = (tender) => {
    setSelectedTender(tender);
    setRfiAnswers(tender.rfi_answers || {});
    setBidSheetData(tender.routes?.map((r, idx) => ({
      route_id: idx + 1,
      ...r,
      complete_bid: false,
      rate: null,
      weekly_capacity: null,
    })) || []);
    setRfiComplete(tender.rfi_submitted || false);
    setBidSheetUnlocked(tender.rfi_submitted || false);
    setActiveTab("rfi");
  };

  // Get progress stats
  const getGatingProgress = () => {
    const gatingQuestions = DEFAULT_RFI_QUESTIONS.filter(q => q.gating);
    const answered = gatingQuestions.filter(q => rfiAnswers[q.id] === "yes").length;
    return { answered, total: gatingQuestions.length };
  };

  const getMandatoryProgress = () => {
    const mandatoryQuestions = DEFAULT_RFI_QUESTIONS.filter(q => q.mandatory);
    const answered = mandatoryQuestions.filter(q => {
      const answer = rfiAnswers[q.id];
      if (!answer) return false;
      return true;
    }).length;
    return { answered, total: mandatoryQuestions.length };
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {!token ? (
        // Public "request access" landing (option c) for external carriers who
        // land here from the marketing site without a portal login.
        <div className="flex min-h-screen items-center justify-center px-4" data-testid="trucking-portal-public">
          <div className="w-full max-w-lg rounded-2xl bg-white p-8 text-center shadow-sm">
            <div className="mx-auto mb-4 w-fit rounded-xl bg-orange-600 p-3 text-white">
              <img src="data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' fill='white' viewBox='0 0 24 24'><path d='M3 4h11v10H3zM14 8h4l3 3v3h-7z'/></svg>" alt="" width="28" height="28" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800">Europacific Carrier Portal</h1>
            <p className="mt-2 text-sm text-slate-600">
              Bid on freight tenders and spot requests. Access is granted to approved carriers only.
            </p>
            <div className="mt-6 grid gap-3">
              <a href="/carrier-registration" data-testid="trucking-portal-register"
                className="rounded-lg bg-orange-600 px-5 py-3 font-semibold text-white hover:bg-orange-700">
                New carrier? Register &amp; request access →
              </a>
              <a href="/login" data-testid="trucking-portal-login"
                className="rounded-lg border border-slate-300 px-5 py-3 font-medium text-slate-700 hover:bg-slate-50">
                Already approved? Sign in
              </a>
            </div>
            <p className="mt-5 text-xs text-slate-400">
              After we approve your registration, you'll receive an email to set your portal password.
            </p>
          </div>
        </div>
      ) : (
      <>
      <TruckingPortalHeader user={user} onLogout={handleLogout} />
      
      <main className="container mx-auto px-4 py-6 pt-20">
        <div className="mb-6">
          <p className="text-slate-600">Manage your tender invitations, RFI responses, and bid submissions</p>
        </div>

        {!selectedTender ? (
          // Tender Invitations List
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Active Tender Invitations</h2>
              <Button variant="outline" size="sm" onClick={fetchInvitations}>
                Refresh
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <Loader2 className="h-8 w-8 mx-auto animate-spin text-muted-foreground" />
              </div>
            ) : tenderInvitations.length === 0 ? (
              <div className="bg-white rounded-xl p-12 border text-center">
                <ClipboardList className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-600 mb-2">No Active Invitations</h3>
                <p className="text-sm text-slate-500">
                  You will see tender invitations here when you are invited to participate
                </p>
              </div>
            ) : (
              <div className="grid gap-4">
                {tenderInvitations.map((tender) => (
                  <div
                    key={tender.id}
                    className="bg-white rounded-xl p-6 border shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => openTender(tender)}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-lg">{tender.name}</h3>
                        <p className="text-sm text-slate-600 mt-1">{tender.description}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {tender.rfi_submitted ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" />
                            RFI Complete
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            RFI Pending
                          </span>
                        )}
                        {tender.bid_submitted && (
                          <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700 flex items-center gap-1">
                            <Check className="h-3 w-3" />
                            Bid Submitted
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="mt-4 flex items-center gap-6 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        Deadline: {new Date(tender.deadline).toLocaleDateString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <FileText className="h-4 w-4" />
                        {tender.routes?.length || 0} routes
                      </span>
                      <span className="flex items-center gap-1">
                        <Building2 className="h-4 w-4" />
                        {tender.owner_name || "Europacific"}
                      </span>
                    </div>
                    
                    <div className="mt-4 flex justify-end">
                      <Button variant="default" size="sm">
                        Open Tender <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          // Tender Detail View
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedTender(null)} className="mb-2">
                  ← Back to Invitations
                </Button>
                <h2 className="text-xl font-bold">{selectedTender.name}</h2>
                <p className="text-slate-600">{selectedTender.description}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-500">Deadline</p>
                <p className="font-semibold">{new Date(selectedTender.deadline).toLocaleDateString()}</p>
              </div>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-2 w-full max-w-md">
                <TabsTrigger value="rfi" className="flex items-center gap-2">
                  <ClipboardList className="h-4 w-4" />
                  RFI Questions
                </TabsTrigger>
                <TabsTrigger value="bidsheet" className="flex items-center gap-2" disabled={!bidSheetUnlocked}>
                  {bidSheetUnlocked ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                  Bid Sheet
                </TabsTrigger>
              </TabsList>

              {/* RFI Tab */}
              <TabsContent value="rfi" className="mt-6">
                <div className="bg-white rounded-xl border shadow-sm">
                  {/* Progress Header */}
                  <div className="p-4 border-b bg-slate-50 rounded-t-xl">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold">Request for Information (RFI)</h3>
                        <p className="text-sm text-slate-600">Complete the RFI to unlock the Bid Sheet</p>
                      </div>
                      <div className="text-right text-sm">
                        <p className="text-slate-600">
                          Gating Questions: <span className="font-semibold text-blue-600">{getGatingProgress().answered}/{getGatingProgress().total}</span>
                        </p>
                        <p className="text-slate-600">
                          Mandatory: <span className="font-semibold">{getMandatoryProgress().answered}/{getMandatoryProgress().total}</span>
                        </p>
                      </div>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="mt-3 h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-500 transition-all"
                        style={{ width: `${(getMandatoryProgress().answered / getMandatoryProgress().total) * 100}%` }}
                      />
                    </div>
                  </div>

                  {/* Gating Notice */}
                  <div className="p-4 bg-amber-50 border-b border-amber-200">
                    <p className="text-sm text-amber-800 flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      <strong>Important:</strong> You must answer "Yes" to all questions marked with 
                      <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-xs ml-1">Bid Sheet Gating</span>
                      to access the Bid Sheet.
                    </p>
                  </div>

                  {/* Questions */}
                  <div className="p-4 space-y-6 max-h-[600px] overflow-y-auto">
                    {DEFAULT_RFI_QUESTIONS.map((question) => (
                      <div key={question.id} className={`p-4 rounded-lg border ${question.gating ? "bg-blue-50 border-blue-200" : "bg-white"}`}>
                        <div className="flex items-start gap-3">
                          <span className="text-sm font-mono text-slate-500 min-w-[40px]">{question.number}</span>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <p className="font-medium text-slate-800">
                                {question.text}
                                {question.mandatory && <span className="text-red-500 ml-1">*</span>}
                              </p>
                              {question.gating && (
                                <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-xs">Bid Sheet Gating</span>
                              )}
                            </div>
                            
                            {question.type === "yes_no" && (
                              <div className="flex gap-4">
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="radio"
                                    name={question.id}
                                    checked={rfiAnswers[question.id] === "yes"}
                                    onChange={() => setRfiAnswers({ ...rfiAnswers, [question.id]: "yes" })}
                                    className="h-4 w-4 text-blue-600"
                                  />
                                  <span className="text-sm">Yes</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="radio"
                                    name={question.id}
                                    checked={rfiAnswers[question.id] === "no"}
                                    onChange={() => setRfiAnswers({ ...rfiAnswers, [question.id]: "no" })}
                                    className="h-4 w-4 text-blue-600"
                                  />
                                  <span className="text-sm">No</span>
                                </label>
                              </div>
                            )}
                            
                            {question.type === "text" && (
                              <Input
                                value={rfiAnswers[question.id] || ""}
                                onChange={(e) => setRfiAnswers({ ...rfiAnswers, [question.id]: e.target.value })}
                                placeholder="Enter your answer..."
                                className="mt-1"
                              />
                            )}
                            
                            {question.type === "number" && (
                              <Input
                                type="number"
                                value={rfiAnswers[question.id] || ""}
                                onChange={(e) => setRfiAnswers({ ...rfiAnswers, [question.id]: e.target.value })}
                                placeholder="Enter number..."
                                className="mt-1 w-40"
                              />
                            )}
                            
                            {question.type === "file" && (
                              <div className="mt-2">
                                <label className="flex items-center gap-2 cursor-pointer text-sm text-blue-600 hover:text-blue-700">
                                  <Upload className="h-4 w-4" />
                                  <span>Upload file</span>
                                  <input type="file" className="hidden" />
                                </label>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Submit Button */}
                  <div className="p-4 border-t bg-slate-50 rounded-b-xl flex justify-between items-center">
                    <p className="text-sm text-slate-600">
                      {checkGatingQuestions() ? (
                        <span className="text-green-600 flex items-center gap-1">
                          <CheckCircle className="h-4 w-4" />
                          All gating questions answered - Bid Sheet will be unlocked
                        </span>
                      ) : (
                        <span className="text-amber-600 flex items-center gap-1">
                          <Lock className="h-4 w-4" />
                          Answer all gating questions with "Yes" to unlock Bid Sheet
                        </span>
                      )}
                    </p>
                    <Button onClick={submitRfi} disabled={submittingRfi || rfiComplete}>
                      {submittingRfi ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : rfiComplete ? (
                        <CheckCircle className="h-4 w-4 mr-2" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      {rfiComplete ? "RFI Submitted" : "Submit RFI"}
                    </Button>
                  </div>
                </div>
              </TabsContent>

              {/* Bid Sheet Tab */}
              <TabsContent value="bidsheet" className="mt-6">
                {!bidSheetUnlocked ? (
                  <div className="bg-white rounded-xl p-12 border text-center">
                    <Lock className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                    <h3 className="text-lg font-medium text-slate-600 mb-2">Bid Sheet Locked</h3>
                    <p className="text-sm text-slate-500 mb-4">
                      Complete the RFI with all gating questions answered "Yes" to unlock
                    </p>
                    <Button variant="outline" onClick={() => setActiveTab("rfi")}>
                      Go to RFI
                    </Button>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl border shadow-sm">
                    {/* Bid Sheet Header */}
                    <div className="p-4 border-b bg-slate-50 rounded-t-xl">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold">Bid Sheet</h3>
                          <p className="text-sm text-slate-600">
                            Download template, fill in your rates, and upload
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={downloadBidTemplate}>
                            <Download className="h-4 w-4 mr-1" />
                            Download Template
                          </Button>
                          <label>
                            <input
                              type="file"
                              accept=".csv,.xlsx"
                              onChange={handleBidUpload}
                              className="hidden"
                            />
                            <Button variant="outline" size="sm" as="span" className="cursor-pointer">
                              <Upload className="h-4 w-4 mr-1" />
                              Upload Bids
                            </Button>
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Instructions */}
                    <div className="p-4 bg-blue-50 border-b text-sm text-blue-800">
                      <p><strong>Instructions:</strong></p>
                      <ol className="list-decimal ml-4 mt-1 space-y-1">
                        <li>Download the bid sheet template (CSV format)</li>
                        <li>For each route you want to bid on, enter "Yes" in "Complete Bid" column</li>
                        <li>Enter your rate in EUR in the "Your Rate" column</li>
                        <li>Enter your weekly capacity for that route</li>
                        <li>Upload the completed file and submit</li>
                      </ol>
                    </div>

                    {/* Bid Data Table */}
                    <div className="p-4 overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-slate-50">
                            <th className="text-left p-2 font-medium">Route</th>
                            <th className="text-left p-2 font-medium">From</th>
                            <th className="text-left p-2 font-medium">To</th>
                            <th className="text-left p-2 font-medium">Equipment</th>
                            <th className="text-left p-2 font-medium">Est. Vol/Week</th>
                            <th className="text-center p-2 font-medium">Bid?</th>
                            <th className="text-right p-2 font-medium">Your Rate (€)</th>
                            <th className="text-right p-2 font-medium">Capacity/Week</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bidSheetData.length === 0 ? (
                            <tr>
                              <td colSpan={8} className="text-center py-8 text-slate-500">
                                Download template and upload your completed bid
                              </td>
                            </tr>
                          ) : (
                            bidSheetData.map((row, idx) => (
                              <tr key={idx} className={`border-b ${row.complete_bid ? "bg-green-50" : ""}`}>
                                <td className="p-2 font-mono text-xs">{row.route_id}</td>
                                <td className="p-2">{row.from}</td>
                                <td className="p-2">{row.to}</td>
                                <td className="p-2">{row.equipment}</td>
                                <td className="p-2">{row.weekly_volume || "-"}</td>
                                <td className="p-2 text-center">
                                  <input
                                    type="checkbox"
                                    checked={row.complete_bid}
                                    onChange={(e) => {
                                      const newData = [...bidSheetData];
                                      newData[idx].complete_bid = e.target.checked;
                                      setBidSheetData(newData);
                                    }}
                                    className="h-4 w-4"
                                  />
                                </td>
                                <td className="p-2">
                                  <Input
                                    type="number"
                                    value={row.rate || ""}
                                    onChange={(e) => {
                                      const newData = [...bidSheetData];
                                      newData[idx].rate = parseFloat(e.target.value) || null;
                                      setBidSheetData(newData);
                                    }}
                                    className="w-24 h-8 text-right"
                                    placeholder="0.00"
                                  />
                                </td>
                                <td className="p-2">
                                  <Input
                                    type="number"
                                    value={row.weekly_capacity || ""}
                                    onChange={(e) => {
                                      const newData = [...bidSheetData];
                                      newData[idx].weekly_capacity = parseInt(e.target.value) || null;
                                      setBidSheetData(newData);
                                    }}
                                    className="w-20 h-8 text-right"
                                    placeholder="0"
                                  />
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Submit Bid */}
                    <div className="p-4 border-t bg-slate-50 rounded-b-xl flex justify-between items-center">
                      <p className="text-sm text-slate-600">
                        {bidSheetData.filter(b => b.complete_bid && b.rate).length} routes with bids
                      </p>
                      <Button onClick={submitBid} disabled={submittingBid}>
                        {submittingBid ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4 mr-2" />
                        )}
                        Submit Bid
                      </Button>
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        )}
      </main>
      </>
      )}
    </div>
  );
}
